@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--qs-space-16);
  padding: var(--qs-space-32) 0;
  animation: fadeIn var(--qs-motion-dur-settle) var(--qs-motion-ease-settle);
}

// The native element carries no visible controls (spec: play/pause/seek are drawn) but must stay
// in the layout — `display:none` risks browsers pausing/discarding an audio element.
.hiddenAudio {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  opacity: 0;
}

.playButton {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--qs-color-glow-core);
  color: var(--qs-color-on-light);
  box-shadow: var(--qs-elev-glow-play);
  cursor: pointer;
  transition: transform var(--qs-motion-dur-fade) var(--qs-motion-ease-settle), box-shadow var(--qs-motion-dur-fade) var(--qs-motion-ease-settle);

  @media (max-width: 719px) {
    width: 64px;
    height: 64px;
  }

  &:hover { transform: scale(1.03); }

  &[data-phase="refused"] {
    box-shadow: 0 0 0 12px var(--qs-color-glow-ring), 0 0 36px 8px color-mix(in srgb, var(--qs-color-glow-core) 24%, transparent);
  }

  &[data-phase="buffering"] {
    animation: breathe var(--qs-motion-breath-in) var(--qs-motion-ease-breath) infinite alternate;
  }

  &[data-phase="ended"] {
    box-shadow: none;
    opacity: 0.7;
  }
}

@keyframes breathe {
  from { opacity: 0.7; }
  to { opacity: 1; }
}

.playIcon {
  width: 28px;
  height: 28px;
}

.title {
  font-family: var(--qs-font-display);
  font-style: italic;
  font-size: var(--qs-type-display-xs-size);
  color: var(--qs-color-text);
  margin: 0;
  text-align: center;
}

.progress {
  display: flex;
  align-items: center;
  gap: var(--qs-space-12);
  width: 100%;
  max-width: 400px;
}

.time {
  font-variant-numeric: tabular-nums;
  font-size: var(--qs-type-caption-size);
  color: var(--qs-color-text-muted);
  flex: none;
  min-width: 36px;
}

.rail,
.railDisabled {
  flex: 1;
  height: 44px;
  appearance: none;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: 3px;
    border-radius: 2px;
    background: var(--qs-color-track);
  }
  &::-moz-range-track {
    height: 3px;
    border-radius: 2px;
    background: var(--qs-color-track);
  }
  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--qs-color-accent);
    box-shadow: var(--qs-elev-glow-thumb);
    margin-top: -4.5px;
  }
  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border: none;
    border-radius: 50%;
    background: var(--qs-color-accent);
  }
}

.rail {
  // --fill is the played fraction, set by the component: gold up to the knob (spec § Anatomy).
  &::-webkit-slider-runnable-track {
    background: linear-gradient(to right, var(--qs-color-accent) var(--fill, 0%), var(--qs-color-track) var(--fill, 0%));
  }
  &::-moz-range-progress {
    height: 3px;
    border-radius: 2px;
    background: var(--qs-color-accent);
  }
}

.railDisabled {
  cursor: default;
  pointer-events: none;

  &::-webkit-slider-runnable-track {
    background-image: linear-gradient(to right, var(--qs-color-track) 0, var(--qs-color-track) 4px, transparent 4px, transparent 8px);
    background-size: 8px 3px;
  }
  &::-webkit-slider-thumb { opacity: 0; }
}

.hint {
  font-family: var(--qs-font-display);
  font-style: italic;
  font-size: var(--qs-type-small-size);
  color: var(--qs-color-text-muted);
  margin: 0;
}

.status {
  font-family: var(--qs-font-display);
  font-size: 0.875rem;
  font-style: italic;
  color: var(--qs-color-error);
  margin: 0;
}

.retry {
  min-height: var(--qs-target-min);
  padding: 0 var(--qs-space-24);
  border-radius: var(--qs-radius-pill);
  border: 1.5px solid var(--qs-color-accent);
  background: transparent;
  color: var(--qs-color-accent);
  cursor: pointer;
}

.eyesButton {
  display: inline-flex;
  align-items: center;
  gap: var(--qs-space-8);
  min-height: var(--qs-target-min);
  padding: 0 var(--qs-space-16);
  border-radius: var(--qs-radius-pill);
  border: 1px solid var(--qs-color-line);
  background: transparent;
  color: var(--qs-color-text-muted);
  font-size: var(--qs-type-small-size);
  cursor: pointer;

  &:hover { color: var(--qs-color-text-soft); border-color: var(--qs-color-line-strong); }
}

// Dock -----------------------------------------------------------------------

.dock {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: var(--qs-layout-player-bar-height);
  display: flex;
  align-items: center;
  gap: var(--qs-space-12);
  padding: 0 var(--qs-layout-gutter-phone);
  background: var(--qs-color-surface-raised);
  border-top: 1px solid var(--qs-color-line);
  z-index: 30;
  animation: dockIn var(--qs-motion-dur-settle) var(--qs-motion-ease-settle);
}

@keyframes dockIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.dockTitle {
  flex: 1;
  font-family: var(--qs-font-display);
  font-style: italic;
  font-size: var(--qs-type-small-size);
  color: var(--qs-color-text-soft);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dockPlay,
.dockEyes {
  flex: none;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.dockPlay {
  background: var(--qs-color-glow-core);
  color: var(--qs-color-on-light);
}

.dockEyes {
  background: transparent;
  color: var(--qs-color-text-muted);
}
